import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Shield, ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { terms_en, terms_ar, privacy_en, privacy_ar } from "../data/consentText";
import { useAuth } from "../context/AuthProvider";
import { apiFetch } from "../utils/apiFetch";

export default function ConsentStep3({ prevStep, onAccept }) {
  const { user, initialized } = useAuth();
  const authUid = user?.id || null;

  const [showModal, setShowModal] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [popup, setPopup] = useState({ visible: false, title: "", message: "" });

  const { i18n } = useTranslation();
  const isArabic = i18n.language.startsWith("ar");

  useEffect(() => {
    if (!authUid) return;
    const saved = localStorage.getItem(`consentAccepted_${authUid}`) === "true";
    if (saved) setChecked(true);
  }, [authUid]);

  const showPopup = (title, message) => {
    setPopup({ visible: true, title, message });
    setTimeout(() => {
      setPopup({ visible: false, title: "", message: "" });
    }, 2000);
  };

  const toggleExpand = (section) => {
    setExpanded(expanded === section ? null : section);
  };

  const saveConsentToDB = async () => {
    try {
      if (!initialized || !authUid) {
        throw new Error("User not authenticated. Please login again.");
      }

      const assessmentId = localStorage.getItem(`current_assessment_id_${authUid}`);
      if (!assessmentId) {
        throw new Error("Assessment ID missing. Please restart questionnaire.");
      }

      setLoading(true);

      const res = await apiFetch(
        `/api/questionnaire/consent?assessmentId=${encodeURIComponent(assessmentId)}`,
        {
          method: "POST",
          body: JSON.stringify({ consent_Terms: true }),
        }
      );

      const raw = await res.text();
      if (!res.ok) throw new Error(raw || "Consent save failed");

      localStorage.setItem(`consentAccepted_${authUid}`, "true");
      setChecked(true);

      showPopup("Saved ✅", "Your consent has been saved");
      onAccept();
    } catch (err) {
      console.error("❌ Consent error:", err.message);
      showPopup("Error ❌", err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = async () => {
    setError("");

    if (!checked) {
      setError("Please accept the Terms & Privacy Policy to continue.");
      return;
    }

    await saveConsentToDB();
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="space-y-6 text-white"
      >
        <h2 className="text-2xl font-bold text-[#EB6AB5] flex items-center gap-2">
          <Shield /> Consent & Privacy
        </h2>

        <p className="text-gray-300">
          We’ll use your answers only to personalize your career insights.
        </p>

        <div className="bg-[#1c1c2e] border border-[#EB6AB5]/30 rounded-xl p-5">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => {
                setChecked(e.target.checked);
                if (e.target.checked) setError("");
              }}
              className="accent-[#EB6AB5] w-5 h-5"
            />
            <span>
              I consent to AI analysis and have read the{" "}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setShowModal(true);
                }}
                className="text-[#EB6AB5] underline"
              >
                Terms & Privacy Policy
              </button>
            </span>
          </label>

          {error && (
            <p className="text-red-400 text-sm mt-3 font-medium">
              {error}
            </p>
          )}
        </div>

        <div className="flex justify-between">
          <button onClick={prevStep} className="text-gray-400 hover:text-white">
            ← Back
          </button>

          <button
            onClick={handleContinue}
            disabled={loading}
            className="px-6 py-2 rounded-full font-semibold bg-[#EB6AB5] text-[#0F0A24] disabled:opacity-60"
          >
            {loading ? "Saving..." : "Continue →"}
          </button>
        </div>
      </motion.div>

      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <motion.div className="bg-[#141428] p-6 rounded-xl max-w-3xl w-full">
            <h3 className="text-xl text-[#EB6AB5] mb-4">Terms & Privacy Policy</h3>

            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <button
                type="button"
                className="flex items-center gap-2 text-[#EB6AB5]"
                onClick={() => toggleExpand("terms")}
              >
                {expanded === "terms" ? <ChevronUp /> : <ChevronDown />} Terms
              </button>

              {expanded === "terms" && (
                <pre className="whitespace-pre-wrap text-sm text-gray-300">
                  {isArabic ? terms_ar : terms_en}
                </pre>
              )}

              <button
                type="button"
                className="flex items-center gap-2 text-[#EB6AB5]"
                onClick={() => toggleExpand("privacy")}
              >
                {expanded === "privacy" ? <ChevronUp /> : <ChevronDown />} Privacy
              </button>

              {expanded === "privacy" && (
                <pre className="whitespace-pre-wrap text-sm text-gray-300">
                  {isArabic ? privacy_ar : privacy_en}
                </pre>
              )}
            </div>

            <div className="flex justify-end gap-4 mt-5">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-4 py-2 rounded bg-gray-700 text-white"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {popup.visible && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/60 z-[9999]">
          <div className="bg-[#1b1b2b] p-6 rounded-xl text-center">
            <h3 className="text-[#EB6AB5] font-bold">{popup.title}</h3>
            <p className="text-gray-300 text-sm">{popup.message}</p>
          </div>
        </div>
      )}
    </>
  );
}